import { Link } from "react-router-dom";
import { ArrowRight, Leaf, RadioTower, Cpu, Scale, Users, Telescope, ArrowUpRight, Building2, Sparkles, Recycle } from "lucide-react";
import { useMemo } from "react";
import { HOME } from "@/constants/testIds";
import { SITE, FOCUS_AREAS, PEAK_BODIES, CONVERGENCE_THEMES, OPEN_DATA_GROUPS } from "@/data/content";
import CTASection from "@/components/shared/CTASection";

const ICONS = {
  leaf: Leaf,
  "radio-tower": RadioTower,
  cpu: Cpu,
  scale: Scale,
  users: Users,
  telescope: Telescope,
};

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`; // eslint-disable-line no-unused-vars

export default function Home() {
  const insights = useMemo(
    () =>
      OPEN_DATA_GROUPS.slice(0, 3).map((g) => ({
        slug: g.slug,
        category: g.label,
        ...g.sources[0],
      })),
    []
  );

  return (
    <div data-testid={HOME.hero}>
      {/* Hero */}
      <section className="relative bg-[#FAFAFA] border-b border-[#E5E7EB] overflow-hidden">
        <div className="ccdr-container py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="ccdr-eyebrow mb-8">AI governance · Policy · Emerging technology</p>
            <h1 className="ccdr-h1 mb-8">
              Resilience,<br />
              <span style={{ color: "var(--ccdr-accent-dark)" }}>decided with evidence.</span>
            </h1>
            <p className="ccdr-lead max-w-2xl mb-10">
              The {SITE.name} brings policymakers, enterprises, researchers, and technology leaders worldwide a
              clear and current view of AI governance, policy, and the emerging technology that increasingly shapes
              how societies prepare for, respond to, and recover from a changing climate and rising disaster risk.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/focus-areas" className="ccdr-btn-primary" data-testid={HOME.heroCtaPrimary}>
                Explore the focus areas <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/insights" className="ccdr-btn-outline" data-testid={HOME.heroCtaSecondary}>
                Read the latest insight
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-[#E5E7EB]">
                <img
                  src="/home-hero.jpg"
                  alt="Governance, standards, and emerging technology for climate and disaster resilience"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5E7EB] p-6 max-w-xs hidden md:block">
                <p className="text-xs uppercase tracking-[0.22em] text-[#B07D4B] mb-2">
                  Anchored to
                </p>
                <p className="text-sm text-[#053043]">
                  Sendai Framework · Paris Agreement · Global Digital Compact · OECD AI Principles
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section data-testid={HOME.trustBand} className="border-b border-[#E5E7EB] bg-white">
        <div className="ccdr-container py-10">
          <p className="ccdr-overline mb-6 text-center">References the work of</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {PEAK_BODIES.map((b) => (
              <span
                key={b.short}
                className="text-[#4B5563] text-sm tracking-[0.15em]"
                title={b.name}
              >
                {b.short}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why this Centre */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="ccdr-eyebrow mb-5">Why this Centre, and why now</p>
            <h2 className="ccdr-h2">A trusted place to decide.</h2>
          </div>
          <div className="lg:col-span-8 ccdr-prose">
            <p>
              Disaster losses are rising faster than the investment made to prevent them. Recent global assessments
              put the direct economic cost of disasters at roughly two hundred billion United States dollars each year,
              while the fuller cost, once ecosystem and informal losses are counted, is estimated at many times higher.
            </p>
            <p>
              At the same time, the instruments meant to manage this risk — the Sendai Framework, the Paris Agreement,
              and the Sustainable Development Goals — are converging, and a new layer of technology governance is forming
              around artificial intelligence. Leaders are being asked to make consequential decisions across all of this at once.
            </p>
            <p>
              The Centre exists to make that decision making easier and better. It tracks the global instruments,
              translates them into language leaders can use, and shows how responsible technology can strengthen
              resilience rather than introduce new fragility.
            </p>
          </div>
        </div>
      </section>

      {/* Focus areas grid */}
      <section className="ccdr-section bg-[#F3F4F6]">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-6">
              <p className="ccdr-eyebrow mb-5">What you will find here</p>
              <h2 className="ccdr-h2">Six pillars, one connected view.</h2>
            </div>
            <div className="lg:col-span-6 flex lg:justify-end items-end">
              <Link to="/focus-areas" className="ccdr-btn-outline">
                View all focus areas <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {FOCUS_AREAS.map((f) => {
              const Icon = ICONS[f.icon] || Leaf;
              return (
                <Link
                  key={f.slug}
                  to={f.deepLink || "/focus-areas#" + f.slug}
                  data-testid={HOME.focusCard(f.slug)}
                  className="bg-white p-8 group transition-colors hover:bg-[#FAFAFA]"
                >
                  <Icon className="w-7 h-7 mb-6" style={{ color: "var(--ccdr-primary)" }} />
                  <h3 className="ccdr-h3 mb-3 group-hover:text-[var(--ccdr-primary)] transition-colors">
                    {f.title}
                  </h3>
                  <p className="text-[#4B5563] leading-relaxed">{f.short}</p>
                  <span
                    className="mt-6 inline-flex items-center gap-1 text-sm"
                    style={{ color: "var(--ccdr-accent-dark)" }}
                  >
                    Open
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Convergence themes */}
      <section className="ccdr-section">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-6">
              <p className="ccdr-eyebrow mb-5">Convergence</p>
              <h2 className="ccdr-h2">Where smart climate action, smart cities, and AI meet.</h2>
            </div>
            <div className="lg:col-span-6 lg:pt-6">
              <p className="ccdr-lead">
                Resilience now sits at the intersection of climate action, urban transformation, and the responsible
                use of AI and emerging technology — and at the convergence of social impact, technology, and sustainability.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {CONVERGENCE_THEMES.map((t, i) => {
              const Icon = [Sparkles, Building2, Cpu, Recycle][i] || Sparkles;
              return (
                <div key={t.name} className="bg-white p-7">
                  <Icon className="w-7 h-7 mb-5" style={{ color: "var(--ccdr-primary)" }} />
                  <h3
                    className="text-base mb-3"
                    style={{ color: "var(--ccdr-primary-dark)", fontWeight: 500 }}
                  >
                    {t.name}
                  </h3>
                  <p className="text-sm text-[#4B5563]">{t.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured open data sources */}
      <section className="ccdr-section">
        <div className="ccdr-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <p className="ccdr-eyebrow mb-5">From the open-data registry</p>
              <h2 className="ccdr-h2">Authoritative data, ready to use.</h2>
            </div>
            <Link to="/insights" className="text-sm ccdr-link">
              All open data sources →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {insights.map((i) => (
              <a
                key={i.url}
                href={i.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HOME.insightCard(i.slug)}
                className="ccdr-card p-8 flex flex-col h-full"
              >
                <span
                  className="text-xs uppercase tracking-[0.22em] mb-4"
                  style={{ color: "var(--ccdr-accent-dark)" }}
                >
                  {i.category}
                </span>
                <h3 className="ccdr-h3 mb-3">{i.name}</h3>
                <p className="text-[#4B5563] flex-1">{i.desc}</p>
                <span
                  className="mt-6 text-[10px] uppercase tracking-[0.22em]"
                  style={{ color: "var(--ccdr-text-muted)" }}
                >
                  Open dataset ↗
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        overline="Take this further"
        title="Read the briefings, or talk to the Centre about a programme."
        primary={{ to: "/get-involved", label: "Get in touch" }}
        secondary={{ to: "/foresight", label: "Or open the Foresight Observatory" }}
      />
    </div>
  );
}
